import { useCallback, useEffect, useRef } from 'react';
import { tokenCache } from './tokenCache';
import { getValidToken } from './auth';
import { fetchAuthSession } from '@aws-amplify/auth';
import Time from '../utils/time';
import { authLogger } from './authLogger';

export const useTokenRefresh = () => {
    const refreshTimeoutRef = useRef<NodeJS.Timeout>();
    const refreshIntervalRef = useRef<NodeJS.Timeout>();

    const refreshToken = async () => {
        try {
            authLogger.tokenRefresh('Starting background refresh');
            // Clear token from cache to force refresh
            tokenCache.clearToken('idToken');
            await getValidToken();
            authLogger.tokenRefresh('Background refresh completed');
        } catch (error) {
            authLogger.error('Background token refresh failed:', error);
        }
    };

    const scheduleTokenRefresh = useCallback((expirationTime: number) => {
        // Clear any existing timeout
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
            authLogger.debug('Cleared existing refresh timeout');
        }
        
        // Calculate when to refresh (10 minutes before expiration)
        const refreshTime = expirationTime - Date.now() - (10 * 60 * 1000);
        
        if (refreshTime > 0) {
            authLogger.tokenRefresh('Scheduled refresh', expirationTime);
            refreshTimeoutRef.current = setTimeout(refreshToken, refreshTime);
        } else {
            // If token is already within refresh window, refresh immediately
            authLogger.tokenRefresh('Token expiring soon, refreshing immediately', expirationTime);
            refreshToken();
        }
    }, []);

    const startTokenMonitoring = useCallback(async () => {
        try {
            authLogger.debug('Starting token monitoring');
            const session = await fetchAuthSession();
            const expiration = session.tokens?.idToken?.payload.exp;
            
            if (expiration) {
                const expirationMs = Time.seconds(expiration);
                scheduleTokenRefresh(expirationMs);
            } else {
                authLogger.warn('No token expiration found in session');
            }
        } catch (error) {
            authLogger.error('Failed to check token expiration:', error);
        }
    }, [scheduleTokenRefresh]);

    useEffect(() => {
        // Start monitoring immediately
        startTokenMonitoring();

        // Set up interval to check token status
        refreshIntervalRef.current = setInterval(startTokenMonitoring, 60000); // Check every minute

        return () => {
            // Cleanup on unmount
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
        };
    }, [startTokenMonitoring]);

    return {
        refreshToken,
        scheduleTokenRefresh
    };
};